import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    listShipments, listCarriers, getSummary,
    FORWARDING_STATUSES, txt, dt, ago,
    INPUT, TH, TD, BTN, Card, Field, Alert, EmptyRow, Spinner, StatusPill, ReadOnlyBadge,
} from './forwardingApi';

// ─────────────────────────────────────────────────────────────────────────────
// Forwarded Orders — list.  READ-ONLY.
//
// One row per forwarded leg. The row is keyed and titled on legBookingId, never
// on fkTaskId: several legs can share a task id, and the task's booking_id does
// not necessarily match the carrier's reference. Treat the task join as a hint,
// not as identity.
//
// This page shows FOUR of the shipment's own columns side by side — our
// forwarding status, the carrier's raw word, what that word was mapped to, and
// when the carrier last spoke. It never reconciles them and never fills one in
// from another: the detail page carries the server-computed three-way
// comparison, and this list is only the way in to it.
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export default function ForwardedShipments() {
    const navigate = useNavigate();

    // ── filters ──────────────────────────────────────────────────────────────
    const [status, setStatus] = useState('');
    const [carrierCode, setCarrierCode] = useState('');
    const [serviceCountry, setServiceCountry] = useState('');
    const [partnerId, setPartnerId] = useState('');
    const [refInput, setRefInput] = useState('');   // what the user is typing
    const [ref, setRef] = useState('');             // debounced value actually queried
    const [page, setPage] = useState(0);

    // ── list state ───────────────────────────────────────────────────────────
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [viewer, setViewer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    // ── carriers dropdown — its own loading/error, never merged with the list ─
    const [carriers, setCarriers] = useState([]);
    const [carriersError, setCarriersError] = useState('');

    // ── summary strip — its own loading/error too ────────────────────────────
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState('');

    // Debounce the free-text reference box (400 ms) so typing does not hammer
    // the proxy, and reset to page 0 whenever the query actually changes.
    useEffect(() => {
        const h = setTimeout(() => {
            setRef(refInput.trim());
            setPage(0);
        }, 400);
        return () => clearTimeout(h);
    }, [refInput]);

    const fetchList = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = { page, size: PAGE_SIZE };
            if (status) params.status = status;
            if (carrierCode) params.carrierCode = carrierCode;
            if (serviceCountry.trim()) params.serviceCountry = serviceCountry.trim();
            if (ref) params.ref = ref;
            if (String(partnerId).trim() !== '' && !isNaN(Number(partnerId))) params.partnerId = Number(partnerId);

            const data = await listShipments(params);
            setRows(Array.isArray(data && data.items) ? data.items : []);
            setTotal(Number((data && data.total) || 0));
            setViewer((data && data.viewer) || null);
        } catch (e) {
            setRows([]);
            setTotal(0);
            setError(e.message || 'Could not load forwarded orders');
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, [page, status, carrierCode, serviceCountry, ref, partnerId]);

    useEffect(() => { fetchList(); }, [fetchList]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const data = await listCarriers();
                if (alive) setCarriers(Array.isArray(data) ? data : []);
            } catch (e) {
                if (alive) {
                    setCarriers([]);
                    setCarriersError(e.message || 'Could not load carriers');
                }
            }
        })();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!carrierCode) {
            setSummary(null);
            setSummaryError('');
            return undefined;
        }
        let alive = true;
        setSummaryLoading(true);
        setSummaryError('');
        (async () => {
            try {
                const data = await getSummary(carrierCode);
                if (alive) setSummary(data || null);
            } catch (e) {
                if (alive) {
                    setSummary(null);
                    setSummaryError(e.message || 'Could not load the carrier summary');
                }
            } finally {
                if (alive) setSummaryLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [carrierCode]);

    const resetFilters = () => {
        setStatus('');
        setCarrierCode('');
        setServiceCountry('');
        setPartnerId('');
        setRefInput('');
        setRef('');
        setPage(0);
    };

    const openDetail = (row) => {
        if (row && row.id != null) navigate(`/orders/forwarded-shipments/${row.id}`);
    };

    const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
    const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
    const to = Math.min(total, (page + 1) * PAGE_SIZE);
    const filtersActive = !!(status || carrierCode || serviceCountry.trim() || ref || String(partnerId).trim());

    return (
        <div style={{ padding: 20, background: '#f8fafc', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
                <i className="fa fa-share-square-o" style={{ fontSize: 22, color: '#0369a1' }} />
                <h2 style={{ margin: 0, fontSize: 22, color: '#0f172a' }}>Forwarded Orders</h2>
                <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                    Legs handed to an external carrier. Read live from the main backend — nothing is copied into the driver
                    database, and nothing on this screen changes anything.
                </span>
                <span style={{ marginLeft: 'auto' }}>
                    <ReadOnlyBadge email={viewer && viewer.email} />
                </span>
            </div>

            {/* ── Filters ── */}
            <Card title="Filters" subtitle="All optional. The reference box searches carrier HAWB, business reference and leg reference.">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                    <Field label="Forwarding status">
                        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} style={INPUT}>
                            <option value="">— Any status —</option>
                            {FORWARDING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Carrier">
                        <select value={carrierCode} onChange={(e) => { setCarrierCode(e.target.value); setPage(0); }} style={INPUT}>
                            <option value="">— Any carrier —</option>
                            {carriers.map((c) => (
                                <option key={txt(c && c.code, '?')} value={(c && c.code) || ''}>
                                    {txt(c && (c.displayName || c.code), '?')}
                                    {c && c.isActive === false ? ' (inactive)' : ''}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Service country">
                        <input
                            value={serviceCountry}
                            onChange={(e) => { setServiceCountry(e.target.value); setPage(0); }}
                            placeholder="e.g. SA"
                            style={INPUT}
                        />
                    </Field>
                    <Field label="Reference contains">
                        <input value={refInput} onChange={(e) => setRefInput(e.target.value)} placeholder="HAWB / leg / business ref" style={INPUT} />
                    </Field>
                    <Field label="Partner id">
                        <input
                            type="number"
                            value={partnerId}
                            onChange={(e) => { setPartnerId(e.target.value); setPage(0); }}
                            placeholder="optional"
                            style={INPUT}
                        />
                    </Field>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                        <button onClick={fetchList} disabled={loading} style={loading ? BTN.disabled : BTN.primary}>
                            {loading ? <><i className="fa fa-spinner fa-spin" /> Loading…</> : <><i className="fa fa-refresh" /> Refresh</>}
                        </button>
                        {filtersActive && (
                            <button onClick={resetFilters} style={BTN.ghost}><i className="fa fa-times" /> Clear</button>
                        )}
                    </div>
                </div>
                {carriersError && <Alert tone="warn">Carrier list unavailable: {carriersError}. You can still filter by everything else.</Alert>}
            </Card>

            {/* ── Carrier summary strip ── */}
            {carrierCode && (
                <Card title={`${carrierCode} — carrier snapshot`}>
                    {summaryLoading && <Spinner label="Loading carrier summary…" />}
                    {!summaryLoading && summaryError && (
                        <Alert tone="warn">Could not load the carrier summary: {summaryError}</Alert>
                    )}
                    {!summaryLoading && !summaryError && !summary && (
                        <div style={{ padding: 18, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                            No summary returned for {carrierCode}.
                        </div>
                    )}
                    {!summaryLoading && !summaryError && summary && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                            <Stat label="Matching these filters" value={total} />
                            <Stat label="Forwarded to this carrier" value={summary.totalShipments != null ? Number(summary.totalShipments) : null} />
                            <Stat label="Needs action" value={summary.needsActionCount != null ? Number(summary.needsActionCount) : null} tone={Number(summary.needsActionCount || 0) > 0 ? 'warn' : 'ok'} />
                            <Stat label="Requests failed" value={summary.requestsFailed != null ? Number(summary.requestsFailed) : null} tone={Number(summary.requestsFailed || 0) > 0 ? 'bad' : 'ok'} />
                            <Stat label="Requests in flight" value={summary.requestsInFlight != null ? Number(summary.requestsInFlight) : null} />
                            <Stat
                                label="Last failure"
                                value={dt(summary.lastFailureAt)}
                                sub={summary.lastFailureAt ? `${ago(summary.lastFailureAt)}${summary.lastFailureOperation ? ` · ${txt(summary.lastFailureOperation)}` : ''}` : 'no failure recorded'}
                                wide
                            />
                        </div>
                    )}
                </Card>
            )}

            {/* ── Table ── */}
            <Card
                title={loaded && !error ? `${total} forwarded leg(s)` : 'Forwarded legs'}
                subtitle={total > 0 ? `Showing ${from}–${to}` : undefined}
            >
                {error && <Alert>{error}</Alert>}

                <div style={{ overflowX: 'auto', marginTop: error ? 12 : 0 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr>
                                <th style={TH}>Leg reference</th>
                                <th style={TH}>Business ref</th>
                                <th style={TH}>Partner</th>
                                <th style={TH}>Carrier</th>
                                <th style={TH}>Our forwarding status</th>
                                <th style={TH}>Carrier says → mapped to</th>
                                <th style={TH}>Carrier AWB</th>
                                <th style={TH}>Attempts</th>
                                <th style={TH}>Last error</th>
                                <th style={TH}>Forwarded at</th>
                                <th style={TH}>Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={11} style={{ padding: 0 }}><Spinner label="Loading forwarded orders…" /></td></tr>
                            )}

                            {!loading && error && (
                                <EmptyRow colSpan={11}>
                                    Nothing could be loaded — the error above explains why. This is <strong>not</strong> an empty result.
                                </EmptyRow>
                            )}

                            {!loading && !error && rows.length === 0 && (
                                <EmptyRow colSpan={11}>
                                    {filtersActive
                                        ? 'No forwarded legs match these filters.'
                                        : 'No orders have been forwarded to an external carrier yet.'}
                                </EmptyRow>
                            )}

                            {!loading && !error && rows.map((r, i) => {
                                const row = r || {};
                                const key = txt(row.legBookingId, `row-${txt(row.id, i)}`);
                                return (
                                    <tr
                                        key={`${key}-${txt(row.id, i)}`}
                                        onClick={() => openDetail(row)}
                                        style={{ cursor: 'pointer' }}
                                        title="Open the full forwarding detail"
                                    >
                                        <td style={{ ...TD, fontWeight: 600 }}>
                                            <span style={{ color: '#2563eb' }}>{txt(row.legBookingId)}</span>
                                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                                #{txt(row.id)} · {txt(row.direction, 'direction ?')} · {txt(row.movementType, 'type ?')}
                                            </div>
                                        </td>
                                        <td style={TD}>{txt(row.businessRefNo)}</td>
                                        <td style={TD}>
                                            {txt(row.partnerName)}
                                            <div style={{ fontSize: 10, color: '#94a3b8' }}>
                                                {row.fkPartnerId != null ? `partner #${row.fkPartnerId}` : ''}
                                            </div>
                                        </td>
                                        <td style={TD}>
                                            {txt(row.carrierCode)}
                                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{txt(row.serviceCountry, '')}</div>
                                        </td>
                                        <td style={TD}><StatusPill status={row.status} /></td>
                                        <td style={TD}>
                                            {row.lastRemoteStatus
                                                ? (
                                                    <>
                                                        <span style={{ fontWeight: 600, fontFamily: 'monospace' }} title="the carrier's own wording">
                                                            {txt(row.lastRemoteStatus)}
                                                        </span>
                                                        <span style={{ color: '#94a3b8' }}> → </span>
                                                        {row.mappedTaskStatus
                                                            ? <span>{txt(row.mappedTaskStatus)}</span>
                                                            : (
                                                                <span
                                                                    style={{ color: '#b45309', fontStyle: 'italic' }}
                                                                    title="This carrier word has no row in the status-mapping table, so nothing was written to our leg from it."
                                                                >
                                                                    unmapped
                                                                </span>
                                                            )}
                                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                                            {row.lastRemoteEventAt ? ago(row.lastRemoteEventAt) : 'no event time'}
                                                        </div>
                                                    </>
                                                )
                                                : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>carrier has said nothing</span>}
                                        </td>
                                        <td style={TD}>
                                            {row.carrierHawb
                                                ? <code style={{ fontSize: 11 }}>{txt(row.carrierHawb)}</code>
                                                : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>none</span>}
                                        </td>
                                        <td style={TD}>
                                            {Number(row.attemptCount || 0)}
                                            <span style={{ color: '#94a3b8' }}> / {row.maxAttempts != null ? Number(row.maxAttempts) : '?'}</span>
                                        </td>
                                        <td style={{ ...TD, color: row.lastErrorCode ? '#dc2626' : '#0f172a' }}>
                                            {row.lastErrorCode
                                                ? (
                                                    <span title={txt(row.lastErrorMsg, 'no message')} style={{ fontWeight: 600 }}>
                                                        {txt(row.lastErrorCode)}
                                                    </span>
                                                )
                                                : <span style={{ color: '#94a3b8' }}>—</span>}
                                        </td>
                                        <td style={TD}>{dt(row.forwardedAt)}</td>
                                        <td style={TD}>
                                            {dt(row.modifiedDate)}
                                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{ago(row.modifiedDate)}</div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* ── Pagination ── */}
                {!error && total > PAGE_SIZE && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                        <span style={{ fontSize: 12, color: '#64748b' }}>Page {page + 1} of {lastPage + 1}</span>
                        <button
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={loading || page <= 0}
                            style={loading || page <= 0 ? BTN.disabled : BTN.ghost}
                        >
                            <i className="fa fa-chevron-left" /> Previous
                        </button>
                        <button
                            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                            disabled={loading || page >= lastPage}
                            style={loading || page >= lastPage ? BTN.disabled : BTN.ghost}
                        >
                            Next <i className="fa fa-chevron-right" />
                        </button>
                    </div>
                )}
            </Card>
        </div>
    );
}

// ── helpers ─────────────────────────────────────────────────────────────────

function Stat({ label, value, sub, tone, wide }) {
    const tones = { ok: '#15803d', warn: '#b45309', bad: '#dc2626' };
    return (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
            <div style={{ fontSize: wide ? 13 : 20, fontWeight: 600, color: tones[tone] || '#0f172a', marginTop: 4 }}>
                {value === null || value === undefined || value === '' ? '—' : value}
            </div>
            {sub ? <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{sub}</div> : null}
        </div>
    );
}
